'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Card } from '@/components/ui/card'
import {
  GUARANTEE_CHECKPOINT_HREF,
  GUARANTEE_FULL_SAT_HREF,
  GUARANTEE_LATER_FULL_SAT_RULE,
  GUARANTEE_SPARSE_CHECKPOINT_RULE,
  baselineResultsHref,
  baselineSourceLabel,
  formatBaselineDate,
} from '@/lib/scores/guarantee-copy'

interface GuaranteeStatusData {
  state: 'no-baseline' | 'no-official' | 'met' | 'short'
  baseline: number | null
  official: number | null
  gain: number | null
  promisedGain: number
  shortfall: number
  isPaid: boolean
  practiceQuestions: number
  minPractice: number
  proofStatus: 'verified' | 'pending' | 'none'
  claimEligible: boolean
  baselineSource?: 'full-sat' | 'checkpoint' | null
  baselineAttemptId?: string | null
  baselineTakenAt?: string | null
  firstCheckpointTooSparse?: boolean
}

/**
 * Personalized score-guarantee status for the signed-in student (roadmap 3.1).
 * Renders nothing for signed-out visitors (the public terms above are enough).
 * Never automates a credit — a claimable shortfall points the student to
 * support, where a human issues the free month.
 */
export default function GuaranteeStatus() {
  const [data, setData] = useState<GuaranteeStatusData | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/guarantee', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) {
          setData(d)
          setLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!loaded || !data) return null

  const { state } = data

  let tone = 'border-border'
  let title = ''
  let body: React.ReactNode = null

  if (state === 'no-baseline') {
    title = 'Set your baseline first'
    body = data.firstCheckpointTooSparse ? (
      <>
        {GUARANTEE_SPARSE_CHECKPOINT_RULE}{' '}
        <Link href={GUARANTEE_FULL_SAT_HREF} className="font-medium text-brand-ink underline">
          Start a full-length practice SAT
        </Link>
      </>
    ) : (
      <>
        Take a two-section Checkpoint, with both Reading and Writing and Math on (answer at least a
        quarter of the questions), or a full-length practice SAT. Separate Math-only and Reading and
        Writing-only Checkpoints don&apos;t set a baseline, even together.
        <span className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <Link href={GUARANTEE_CHECKPOINT_HREF} className="font-medium text-brand-ink underline">
            Start a two-section Checkpoint
          </Link>
          <Link href={GUARANTEE_FULL_SAT_HREF} className="font-medium text-brand-ink underline">
            Or a full-length practice SAT
          </Link>
        </span>
      </>
    )
  } else if (state === 'no-official') {
    title = 'Add your official score to track your guarantee'
    body =
      'Once you get your real College Board score back, enter it below — and upload the score report so it can count toward the guarantee.'
  } else if (state === 'met') {
    // The display score may be self-reported/pending (a claim never is) — say
    // so instead of presenting an unverified number as the confirmed official
    // score (founder report 2026-07-06: an admin-UNverified score read as
    // official here).
    if (data.proofStatus === 'verified') {
      tone = 'border-emerald-500/40 bg-emerald-500/5'
      title = `🎉 You're up +${data.gain} points`
      body = `Your official ${data.official} beat your ${data.baseline} baseline by more than our +${data.promisedGain} promise. Nicely done.`
    } else {
      tone = 'border-emerald-500/40 bg-emerald-500/5'
      title = `You're up +${data.gain} points (pending verification)`
      body =
        data.proofStatus === 'pending'
          ? `Your entered ${data.official} beats your ${data.baseline} baseline by more than our +${data.promisedGain} promise — great sign. It'll count as official once your score report is verified.`
          : `Your entered ${data.official} beats your ${data.baseline} baseline by more than our +${data.promisedGain} promise — great sign. Upload your score report below to make it official.`
    }
  } else {
    // short
    const enoughPractice = data.practiceQuestions >= data.minPractice
    if (!data.isPaid) {
      title = `You're up +${data.gain} points`
      body = `The +${data.promisedGain}-point guarantee covers Pro members. Your official ${data.official} vs your ${data.baseline} baseline is +${data.gain} so far.`
    } else if (!enoughPractice) {
      const remaining = data.minPractice - data.practiceQuestions
      tone = 'border-brand/40 bg-brand/5'
      title = `You're up +${data.gain} — keep going`
      body = `You're ${data.shortfall} short of the +${data.promisedGain} promise. To stay covered by the guarantee, keep practicing — about ${remaining} more questions and you'll have done enough for us to make it right if you still fall short.`
    } else if (data.proofStatus === 'verified') {
      tone = 'border-amber-500/50 bg-amber-500/5'
      title = `You're covered by the guarantee`
      body = `Your verified official ${data.official} is +${data.gain} over your ${data.baseline} baseline — ${data.shortfall} short of our +${data.promisedGain} promise, and you put in the work. Email support to claim your free month.`
    } else if (data.proofStatus === 'pending') {
      tone = 'border-brand/40 bg-brand/5'
      title = `Your score report is under review`
      body = `You're +${data.gain} (${data.shortfall} short of the +${data.promisedGain} promise) and you've done the work. We're verifying your uploaded report — once confirmed, you can claim your free month.`
    } else {
      tone = 'border-amber-500/50 bg-amber-500/5'
      title = `Upload your score report to claim`
      body = `You're +${data.gain}, ${data.shortfall} short of our +${data.promisedGain} promise, and you've put in the work. To make a guarantee claim, upload your official College Board score report below — a typed score alone can't be claimed.`
    }
  }

  // Where the baseline came from, once there is one — a bare number left the
  // student unable to tell which test it was (external QA).
  const source = data.baseline != null ? data.baselineSource : null
  const taken = data.baselineTakenAt ? formatBaselineDate(data.baselineTakenAt) : ''

  return (
    <Card className={`p-5 ${tone}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Your guarantee status
      </p>
      <h3 className="mt-1 text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      {source && (
        <div
          className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground"
          data-testid="guarantee-baseline-source"
        >
          <p>
            Your baseline: <span className="font-semibold text-foreground">{data.baseline}</span>,
            from{' '}
            {data.baselineAttemptId ? (
              <Link
                href={baselineResultsHref(source, data.baselineAttemptId)}
                className="underline hover:text-foreground"
              >
                {baselineSourceLabel(source)}
              </Link>
            ) : (
              baselineSourceLabel(source)
            )}
            {taken ? ` on ${taken}` : ''}.
          </p>
          {source === 'checkpoint' && <p className="mt-1">{GUARANTEE_LATER_FULL_SAT_RULE}</p>}
        </div>
      )}
    </Card>
  )
}
